import { noop, sortBy } from "lodash-es";

import { writer } from "@/backend/write/BackendWriter";
import { isEqual } from "@/math/coordinates";
import type { BoardCard, PositionedCard } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import type { SelectionAction, SelectionState } from "@/model/selection";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useSelectionStore } from "@/store/selection";
import { ensureResolve } from "@/utils/async/utils";

import { internalActions } from "./internalActions";
import type { SelectionScope } from "./selectionActions";
import type { ActionContext } from "./types";

export function cardsInScope(scope: SelectionScope) {
  return scope === "selection"
    ? useBoardStore().selectedStickies
    : useBoardStore().contextMenuRegionStickies();
}

export async function execute<T extends SelectionState>(
  ctx: ActionContext,
  selectionScope: SelectionScope,
  action: SelectionAction<T>,
) {
  const cards = cardsInScope(selectionScope);
  const stateBefore = action.saveState(cards);
  const res = (action.execute(cards, stateBefore) || Promise.resolve()).then(() =>
    useSelectionStore().addHistory({ stateBefore, stateAfter: action.saveState(cards), action }),
  );
  if (!ctx.tracked) {
    ctx.track({ selectionScope });
  }
  return res;
}

type Sorter = (card: PositionedCard) => number;
const sortByPosition: Sorter[] = [(card) => card.meta.pos.y, (card) => card.meta.pos.x];
export function sortByDirection(dir: RelativeCoordinate): Sorter {
  return Math.abs(dir.x) > 0
    ? (card) => card.meta.pos.x * Math.sign(dir.x)
    : (card) => card.meta.pos.y * Math.sign(dir.y);
}

export function setCardPositions(
  cards: PositionedCard[],
  sorter: Sorter | Sorter[] = sortByPosition,
) {
  const boardId = useBoardStore().currentBoard().id;
  return ensureResolve((resolver) => {
    const sorted = sortBy(cards, sorter);
    sorted.forEach((card, index) => {
      const pos = { id: card.data.id, boardId, ...card.meta.pos };
      useBoardsStore().setCardPos(pos, index === cards.length - 1 ? resolver() : noop);
      useBoardsStore().cardToFront({ id: card.data.id, boardId });
    });
    sorted.forEach((card) => {
      internalActions.moveCard(boardId, card.data.id, card.meta.pos, true);
    });
    writer.frontCard(
      boardId,
      sorted.map((card) => card.data.id),
    );
  });
}

export const alwaysUndoable = {
  stateChanged: () => false,
};

export const cardPositionState = {
  saveState: saveCardPositions,
  resetToState: resetCardPositions,
  stateChanged: cardPositionChanged,
};

export function saveCardPositions(cards: BoardCard[]): PositionedCard[] {
  return cards.map((card) => ({
    data: { id: card.data.id },
    meta: { pos: { ...card.meta.pos }, zIndex: card.meta.zIndex },
  }));
}

export function resetCardPositions(cardStates: PositionedCard[]) {
  const boardId = useBoardStore().currentBoard().id;
  return ensureResolve((resolver) => {
    cardStates.forEach((card, index) => {
      const pos = { id: card.data.id, boardId, ...card.meta.pos };
      useBoardsStore().setCardPos(pos, index === cardStates.length - 1 ? resolver() : noop);
      useBoardsStore().cardToFront({ id: card.data.id, boardId, zIndex: card.meta.zIndex });
      internalActions.moveCard(boardId, card.data.id, card.meta.pos, true);
      writer.alterCard(boardId, card.data.id, { zIndex: card.meta.zIndex });
    });
  });
}

export function cardPositionChanged(cardStates: PositionedCard[]) {
  const cards = useBoardStore().currentBoard().cards;
  return cardStates.some((state) => {
    const pos = cards[state.data.id]?.meta.pos;
    return !pos || !isEqual(pos, state.meta.pos, 0.0001);
  });
}
